import React, { useEffect, useState } from 'react';

import { useCapabilities, featureCapability } from '../../../ext-core';

import { useSemanticSearch } from '../../hooks/useSemanticSearch';
import { RecommendedCard } from '../RecommendedCard/RecommendedCard';
import css from './RecommendedSection.module.css';

// How many skeleton cards to show while loading.
const SKELETON_COUNT = 4;

/**
 * Renders a "You might also like" / semantic recommended section below search
 * results. This is the PUBLIC surface for the extension (TASK157 D-OPEN-2):
 * the component owns the full fetch → dedup → hydrate → render pipeline.
 * Marketplace code injects primitives only; no orchestration logic lives in
 * the host.
 *
 * --- SELF-GATES ---
 * Renders nothing (no chrome, no skeleton) when:
 *   - capabilities.aiSearch.enabled is false (or capabilities haven't loaded yet
 *     — we wait for ready before showing anything, so there's no flash)
 *   - `keywords` is absent or blank
 *   - search returned 0 results after dedup (hide-when-empty)
 *
 * --- MODE SELECTION (D-OPEN-4 / D4) ---
 * Mode A (native, primary — Sharetribe marketplaces):
 *   `ListingCard` + `hydrate` props provided AND capabilities.aiSearch.renderMode
 *   is NOT 'standalone' → uses the host's own card component with hydrated SDK
 *   entities for pixel-identical results to the main search grid.
 *
 * Mode B (self-contained fallback):
 *   `ListingCard`/`hydrate` absent, OR capabilities.aiSearch.renderMode ===
 *   'standalone' (dashboard override) → renders `<RecommendedCard>` from the
 *   raw API data (title/price/imageUrls). Requires `listingUrl` for click-through.
 *
 * --- i18n (D-OPEN-3) ---
 * No locale files are bundled. Pass the translated heading as the `heading` prop:
 *   Mode A: host passes its own `<FormattedMessage id="SearchPage.semanticResultsHeading"/>`
 *   Mode B: `heading` prop string, or the default English fallback below.
 *
 * --- PROPS ---
 * @param {string}   keywords         - the current search query
 * @param {string[]} [excludeIds]     - ids already shown in keyword results (D5/TASK154)
 * @param {React.ComponentType} [ListingCard] - host's card component (Mode A)
 * @param {(ids: string[]) => Promise<object[]>} [hydrate] - host SDK hydration fn (Mode A)
 * @param {(listing: object) => string} [listingUrl] - URL builder for Mode B click-through
 * @param {React.ReactNode} [heading] - section heading (i18n-aware; string or element)
 * @param {number}   [limit]          - max results to request (server may cap lower)
 * @param {string}   [rootUrl]        - override the host API base URL (advanced)
 * @param {string}   [className]      - additional class on the section root
 */
export const RecommendedSection = ({
  keywords,
  excludeIds,
  ListingCard,
  hydrate,
  listingUrl,
  heading,
  limit,
  rootUrl,
  className,
}) => {
  // --- Capabilities gate (D3) ---
  const { capabilities, ready: capsReady } = useCapabilities({ rootUrl });
  const aiSearch = featureCapability(capabilities, 'aiSearch');

  // --- Mode selection (D-OPEN-4 / D4) ---
  // Auto: ListingCard+hydrate injected => Mode A, else Mode B.
  // Dashboard override: capabilities.aiSearch.renderMode === 'standalone' forces Mode B
  // even when a native card is injected.
  const hasNativeCard = typeof ListingCard === 'function' && typeof hydrate === 'function';
  const renderMode = aiSearch.renderMode; // 'native' | 'standalone' | undefined
  const isModeB = !hasNativeCard || renderMode === 'standalone';

  // --- Semantic search hook ---
  const { results: rawResults, loading } = useSemanticSearch(
    // Don't fetch at all if not enabled yet (capsReady=false means we haven't
    // confirmed enabled status; pass empty to suppress the fetch).
    capsReady && aiSearch.enabled ? keywords : '',
    { excludeIds, limit, rootUrl }
  );

  // Stable dependency key for the hydration effect — mirrors the `dependencyKey`
  // pattern in `useEnrichments` and `useSemanticSearch` (no inline .join in deps).
  const resultIdsKey = rawResults.map(r => r.listingId).join(',');

  // --- Hydration state (Mode A only) ---
  const [hydratedListings, setHydratedListings] = useState([]);
  const [hydrating, setHydrating] = useState(false);

  useEffect(() => {
    // Mode B: no hydration needed; reset to avoid stale state.
    if (isModeB || rawResults.length === 0) {
      setHydratedListings([]);
      setHydrating(false);
      return;
    }

    // Mode A: call the host's hydrate fn with the result ids.
    let isActive = true;
    setHydrating(true);

    const ids = rawResults.map(r => r.listingId);

    Promise.resolve()
      .then(() => hydrate(ids))
      .then(entities => {
        if (isActive) {
          setHydratedListings(Array.isArray(entities) ? entities : []);
          setHydrating(false);
        }
      })
      .catch(() => {
        if (isActive) {
          setHydratedListings([]);
          setHydrating(false);
        }
      });

    return () => {
      isActive = false;
    };
    // `hydrate` is intentionally omitted from deps — it is a stable function
    // reference injected from the host. Including it would trigger re-hydration
    // on every render if the host passes an inline arrow (common pattern).
    // `rawResults` is tracked via `resultIdsKey` (stable string, not array ref).
    // eslint pattern note: no exhaustive-deps disable directive per CONTRIBUTING.md.
  }, [resultIdsKey, isModeB]);

  // --- Render-nothing guards ---

  // Wait for capabilities before rendering anything (prevents flash of content
  // that immediately disappears when aiSearch.enabled is false).
  if (!capsReady) {
    return null;
  }

  // Not enabled for this tenant.
  if (!aiSearch.enabled) {
    return null;
  }

  // No keywords → nothing to recommend.
  if (!keywords || !keywords.trim()) {
    return null;
  }

  // Still loading (either the search or hydration).
  const isLoading = loading || hydrating;

  // Show skeleton while loading.
  if (isLoading) {
    return (
      <section className={[css.section, className].filter(Boolean).join(' ')}>
        {heading !== undefined ? (
          <h3 className={css.heading}>{heading}</h3>
        ) : (
          <h3 className={css.heading}>You might also like</h3>
        )}
        <div className={css.skeletonGrid} aria-hidden="true">
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <div key={i} className={css.skeletonCard}>
              <div className={css.skeletonImage} />
              <div className={css.skeletonBody}>
                <div className={css.skeletonTitle} />
                <div className={css.skeletonPrice} />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // Determine what to render based on mode.
  const modeAListings = isModeB ? [] : hydratedListings;
  const modeBResults = isModeB ? rawResults : [];

  // Hide when empty (both modes).
  const hasContent = isModeB ? modeBResults.length > 0 : modeAListings.length > 0;
  if (!hasContent) {
    return null;
  }

  const headingNode =
    heading !== undefined ? (
      <h3 className={css.heading}>{heading}</h3>
    ) : (
      <h3 className={css.heading}>You might also like</h3>
    );

  return (
    <section className={[css.section, className].filter(Boolean).join(' ')}>
      {headingNode}

      {isModeB ? (
        // Mode B: render self-contained RecommendedCard from raw API data.
        <div className={css.grid}>
          {modeBResults.map(listing => (
            <RecommendedCard
              key={listing.listingId}
              listing={listing}
              listingUrl={listingUrl}
            />
          ))}
        </div>
      ) : (
        // Mode A: render the host's own ListingCard with hydrated SDK entities.
        <div className={css.grid}>
          {modeAListings.map(listing => (
            <ListingCard key={listing.id ? listing.id.uuid : listing.id} listing={listing} />
          ))}
        </div>
      )}
    </section>
  );
};
